import { apiClient } from '@isocel/front-common';

/**
 * Fetch a signed URL for a remote file stored in a private GCS bucket
 * @param fileId - The ID of the remote file
 * @returns Object containing the signed URL and expiration time in seconds
 */
export const fetchSignedUrl = async (fileId: string): Promise<{ signedUrl: string; expiresIn: number }> => {
    const { data } = await apiClient().get(`/files/${fileId}/signed-url`);
    return data;
};

/**
 * Result of a batch signed URL fetch for a single file
 */
export interface SignedUrlResult {
    fileId: string;
    signedUrl?: string;
    expiresIn?: number;
    error?: string;
}

/**
 * Response from the batch signed URLs endpoint
 */
export interface BatchSignedUrlsResponse {
    results: SignedUrlResult[];
    total: number;
    successful: number;
    failed: number;
}

/**
 * Fetch signed URLs for multiple files in a single API call (optimized)
 * @param fileIds - Array of file IDs (max 100 per request)
 * @returns Object containing results for each file, with statistics
 */
export const fetchSignedUrlsBatch = async (fileIds: string[]): Promise<BatchSignedUrlsResponse> => {
    if (fileIds.length === 0) {
        return { results: [], total: 0, successful: 0, failed: 0 };
    }

    if (fileIds.length > 100) {
        throw new Error('Maximum 100 file IDs per request');
    }

    const { data } = await apiClient().post<BatchSignedUrlsResponse>('/files/signed-urls', {
        fileIds,
    });

    return data;
};
